import React, { useState } from 'react';
import { ROLES, PHASES } from '../data/roles';
import { SECTIONS } from '../data/sections';
import { getSectionProgress, getPctColor, pillClass, numClass } from '../utils/progress';
import { PROCESS_META } from '../data/processMeta';
import { exportProgressCSV } from '../utils/exportUtils';
import './SectionScreen.css';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Done', 'Blocked', 'N/A'];

export default function SectionScreen({ state, sectionKey, onSetStep, onNavSection }) {
  const sec = SECTIONS[sectionKey];
  const role = ROLES[state.role];
  if (!sec || !role) return null;

  const sp   = getSectionProgress(state.progress, sectionKey);
  const pc   = getPctColor(sp.pct, role.color);
  const meta = PROCESS_META[sectionKey] || {};

  const ph = PHASES.find(p => p.keys.includes(sectionKey));
  const roleSections = role.sections;
  const idx = roleSections.indexOf(sectionKey);

  return (
    <div className="page-content">
      <div className="sec-header">
        <div style={{flex:1}}>
          <div className="sec-badge-large" style={{ background: role.light, color: role.color }}>{sectionKey}</div>
          <div className="sec-title-large">{sec.title}</div>
          <div className="sec-phase-label">{ph ? `${ph.code} — ${ph.title}` : 'Cross-Cutting — All Phases'}</div>
          {meta.prerequisites?.length > 0 && (
            <div className="sec-meta-row">
              <i className="ti ti-arrow-back-up" style={{fontSize:11}} aria-hidden="true" />
              <span>Prerequisites: {meta.prerequisites.join(', ')}</span>
            </div>
          )}
          {meta.parallel?.filter(p => role.sections.includes(p)).length > 0 && (
            <div className="sec-meta-row sec-meta-parallel">
              <i className="ti ti-arrows-right-left" style={{fontSize:11}} aria-hidden="true" />
              <span>Runs in parallel with: {meta.parallel.filter(p => role.sections.includes(p)).join(', ')}</span>
            </div>
          )}
          {meta.critical && (
            <div className="sec-meta-row sec-meta-critical">
              <i className="ti ti-alert-triangle" style={{fontSize:11}} aria-hidden="true" />
              <span>Critical path section</span>
            </div>
          )}
        </div>
        <button className="btn btn-sm" onClick={() => exportProgressCSV(state, state.role)} title="Export progress">
          <i className="ti ti-download" aria-hidden="true" />
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-num" style={{ color: pc }}>{sp.pct}%</div>
          <div className="stat-lbl">Complete</div>
        </div>
        <div className="stat-box">
          <div className="stat-num" style={{ color: '#375623' }}>{sp.done}</div>
          <div className="stat-lbl">Done</div>
        </div>
        <div className="stat-box">
          <div className="stat-num" style={{ color: '#854F0B' }}>{sp.inProg}</div>
          <div className="stat-lbl">In progress</div>
        </div>
        <div className="stat-box">
          <div className="stat-num" style={{ color: '#A32D2D' }}>{sp.blocked}</div>
          <div className="stat-lbl">Blocked</div>
        </div>
      </div>

      <div className="pbar" style={{ marginBottom: '1rem', height: 6, borderRadius: 3 }}>
        <div className="pfill" style={{ width: `${sp.pct}%`, background: pc }} />
      </div>

      <div className="steps-list">
        {sec.checklist.map(([step, owner], i) => (
          <StepItem
            key={i}
            index={i}
            step={step}
            owner={owner}
            stepData={state.progress?.[sectionKey]?.[i] || {}}
            onUpdate={(field, value) => onSetStep(sectionKey, i, field, value)}
          />
        ))}
      </div>

      <div className="sec-nav">
        {idx > 0 && (
          <button className="btn" onClick={() => onNavSection(roleSections[idx - 1])}>
            <i className="ti ti-arrow-left" aria-hidden="true" />
            Previous section
          </button>
        )}
        {idx < roleSections.length - 1 && (
          <button className="btn btn-primary" onClick={() => onNavSection(roleSections[idx + 1])}>
            Next section
            <i className="ti ti-arrow-right" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function StepItem({ index, step, owner, stepData, onUpdate }) {
  const [open, setOpen] = useState(false);
  const status = stepData.status || 'Not Started';

  return (
    <div className="step-item">
      <div className="step-head" onClick={() => setOpen(o => !o)}>
        <div className={numClass(status)}>{index + 1}</div>
        <div className="step-body">
          <div className="step-text">{step}</div>
          <div className="step-owner">
            <i className="ti ti-user" style={{ fontSize: 10 }} aria-hidden="true" />
            {owner}
          </div>
        </div>
        <span className={pillClass(status)}>{status}</span>
        <i
          className="ti ti-chevron-down"
          style={{
            fontSize: 12,
            color: '#9E9E9E',
            flexShrink: 0,
            marginLeft: 4,
            transform: open ? 'rotate(180deg)' : '',
            transition: 'transform 0.18s',
          }}
          aria-hidden="true"
        />
      </div>

      {open && (
        <div className="step-detail">
          <div className="step-fields">
            <div className="step-field">
              <label>Status</label>
              <select
                value={status}
                onChange={e => onUpdate('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="step-field">
              <label>Date completed</label>
              <input
                type="date"
                value={stepData.date || ''}
                onChange={e => onUpdate('date', e.target.value)}
              />
            </div>
            <div className="step-field step-field--full">
              <label>Notes</label>
              <textarea
                placeholder="Add notes..."
                defaultValue={stepData.notes || ''}
                onBlur={e => onUpdate('notes', e.target.value)}
              />
            </div>
            <div className="step-field step-field--full">
              <label>Evidence / Reference</label>
              <input
                type="text"
                placeholder="Document name, link, or reference..."
                defaultValue={stepData.evidence || ''}
                onBlur={e => onUpdate('evidence', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
